import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { listingAPI, vendorAPI } from '../../services/api';
import { Listing, ListingFilters } from '../../types';

interface ListingsState {
  listings: Listing[];
  currentListing: Listing | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: ListingsState = {
  listings: [],
  currentListing: null,
  loading: false,
  error: null,
  pagination: null,
};

// Async thunks
export const fetchListings = createAsyncThunk(
  'listings/fetchListings',
  async (filters: ListingFilters = {}, { rejectWithValue }) => {
    try {
      const params: Record<string, string | number> = {
        page: filters.page ?? 1,
        limit: filters.limit ?? 10,
        sortBy: filters.sortBy ?? 'createdAt',
        sortOrder: filters.sortOrder ?? 'desc',
      };
      if (filters.status) params.status = filters.status;
      if (filters.store) params.store = filters.store;
      if (filters.search?.trim()) params.search = filters.search.trim();

      const response = await vendorAPI.getListings(params);
      const body = response.data as {
        data?: { listings: Listing[] };
        pagination?: ListingsState['pagination'];
      };
      return {
        listings: body.data?.listings ?? [],
        pagination: body.pagination ?? null,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch listings';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchListingById = createAsyncThunk(
  'listings/fetchListingById',
  async (listingId: string, { rejectWithValue }) => {
    try {
      const response = await listingAPI.getById(listingId);
      return (response.data as any).data.listing;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch listing';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createListing = createAsyncThunk(
  'listings/createListing',
  async (listingData: any, { rejectWithValue }) => {
    try {
      const response = await listingAPI.create(listingData);
      return (response.data as any).data.listing;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create listing';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateListing = createAsyncThunk(
  'listings/updateListing',
  async ({ listingId, listingData }: { listingId: string; listingData: any }, { rejectWithValue }) => {
    try {
      const response = await listingAPI.update(listingId, listingData);
      return (response.data as any).data.listing;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update listing';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteListing = createAsyncThunk(
  'listings/deleteListing',
  async (listingId: string, { rejectWithValue }) => {
    try {
      await listingAPI.delete(listingId);
      return listingId;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete listing';
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadListingImages = createAsyncThunk(
  'listings/uploadListingImages',
  async ({ listingId, formData }: { listingId: string; formData: FormData }, { rejectWithValue }) => {
    try {
      await listingAPI.uploadImages(listingId, formData);
      return listingId;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to upload images';
      return rejectWithValue(errorMessage);
    }
  }
);

const listingsSlice = createSlice({
  name: 'listings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentListing: (state, action) => {
      state.currentListing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch listings
      .addCase(fetchListings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.loading = false;
        state.listings = action.payload.listings;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchListings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch listing by ID
      .addCase(fetchListingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchListingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentListing = action.payload;
        state.error = null;
      })
      .addCase(fetchListingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create listing
      .addCase(createListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createListing.fulfilled, (state, action) => {
        state.loading = false;
        state.listings.unshift(action.payload);
        state.error = null;
      })
      .addCase(createListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update listing
      .addCase(updateListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateListing.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.listings.findIndex(listing => listing._id === action.payload._id);
        if (index !== -1) {
          state.listings[index] = action.payload;
        }
        if (state.currentListing?._id === action.payload._id) {
          state.currentListing = action.payload;
        }
        state.error = null;
      })
      .addCase(updateListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete listing
      .addCase(deleteListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteListing.fulfilled, (state, action) => {
        state.loading = false;
        state.listings = state.listings.filter(listing => listing._id !== action.payload);
        if (state.currentListing?._id === action.payload) {
          state.currentListing = null;
        }
        state.error = null;
      })
      .addCase(deleteListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload listing images
      .addCase(uploadListingImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadListingImages.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(uploadListingImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentListing } = listingsSlice.actions;
export default listingsSlice.reducer;
